import React, { useEffect, useState, useRef } from 'react';

import { pointsToFlatArray, updatePointCoordinates, moveAllPoints, generateId, generateDefaultPolygon } from '../utils/coordinateUtils';
import { Card } from 'src/components/Card';
import { Button } from 'src/components/Button';
import { AiOutlinePlus, AiOutlineSave, AiOutlineDelete } from 'react-icons/ai';
import { Polygon } from '../types';
import DrawAnnotations from './draw';


export interface DrawComponentProps {
  initialPolygons?: Polygon[];
  canvasWidth?: number;
  canvasHeight?: number;
  imageBackground?: string | null;
  onPolygonsSave?: (polygons: Polygon[]) => void;
  onDeletePolygon?: (polygon: Polygon) => Promise<boolean> | boolean;
  title?: string;
}

export const DrawComponent: React.FC<DrawComponentProps> = ({
  initialPolygons = [],
  canvasWidth = 550,
  canvasHeight = 350,
  imageBackground = null,
  onPolygonsSave,
  onDeletePolygon,
  title = 'Areas Distribution'
}) => {
  const [polygons, setPolygons] = useState<Polygon[]>(initialPolygons);
  const [activePolygonIndex, setActivePolygonIndex] = useState(-1);
  const [newPolygonName, setNewPolygonName] = useState('');
  const [hasChanges, setHasChanges] = useState(true);

  useEffect(() => {
    if (initialPolygons.length > 0 && polygons.length === 0) {
      setPolygons(initialPolygons);
    }
  }, [initialPolygons, polygons.length]);

  const handleAddPolygon = () => {
    const newPolygon = generateDefaultPolygon(newPolygonName || `Area ${polygons.length + 1}`);

    const updatedPolygons = polygons.map((polygon) => ({
      ...polygon,
      isSelected: false,
    }));

    const newPolygons = [...updatedPolygons, newPolygon];
    setPolygons(newPolygons);
    setActivePolygonIndex(updatedPolygons.length);
    setNewPolygonName('');
    setHasChanges(true);
    
    if (onPolygonsSave) {
      onPolygonsSave(newPolygons);
    }
  };

  const handlePolygonChange = (updatedPolygons: Polygon[]) => {
    setPolygons(updatedPolygons);
    setHasChanges(true);
    if (onPolygonsSave) {
      onPolygonsSave(updatedPolygons);
    }
  };

  useEffect(() => {
    if (polygons.length > 0) {
      setHasChanges(true);
    }
  }, [polygons]);

  useEffect(() => {
    if (hasChanges && onPolygonsSave) {
      onPolygonsSave(polygons);
    }
  }, [hasChanges, polygons, onPolygonsSave]);

  const handleSelectPolygon = (index: number) => {
    if (index < 0) {
      setPolygons(polygons.map((polygon) => ({
        ...polygon,
        isSelected: false,
      })));
      setActivePolygonIndex(-1);
      return;
    }

    if (index >= polygons.length) return;

    const updatedPolygons = polygons.map((polygon, idx) => ({
      ...polygon,
      isSelected: idx === index,
    }));

    setPolygons(updatedPolygons);
    setActivePolygonIndex(index);
  };

  const handleDeletePolygon = async () => {
    if (activePolygonIndex < 0 || activePolygonIndex >= polygons.length) return;
    
    const polygonToDelete = polygons[activePolygonIndex];

    if (onDeletePolygon && polygonToDelete) {
      try {
        const deleted = await onDeletePolygon(polygonToDelete);
        
        if (deleted) {
          const updatedPolygons = [...polygons];
          updatedPolygons.splice(activePolygonIndex, 1);
          
          setPolygons(updatedPolygons);
          setHasChanges(true);
          
          if (activePolygonIndex >= updatedPolygons.length) {
            setActivePolygonIndex(updatedPolygons.length - 1);
          }
          
          if (onPolygonsSave) {
            onPolygonsSave(updatedPolygons);
          }
        }
      } catch (error) {
        console.error('Error deleting polygon:', error);
      }
    } else {
      const updatedPolygons = [...polygons];
      updatedPolygons.splice(activePolygonIndex, 1);

      setPolygons(updatedPolygons);
      setHasChanges(true);

      if (activePolygonIndex >= updatedPolygons.length) {
        setActivePolygonIndex(updatedPolygons.length - 1);
      }
      
      if (onPolygonsSave) {
        onPolygonsSave(updatedPolygons);
      }
    }
  };

  const handleSavePolygons = () => {
    if (onPolygonsSave) {
      onPolygonsSave(polygons);
    }
    setHasChanges(false);
  };

  const handlePolygonNameChange = (index: number, newName: string) => {
    setPolygons((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        name: newName,
      };
      
      if (onPolygonsSave) {
        onPolygonsSave(updated);
      }
      
      return updated;
    });
    setHasChanges(true);
  };

  return (
    <Card title={title} className="pl-6">
      <div className="flex gap-4">
        <DrawAnnotations
          polygons={polygons}
          activePolygonIndex={activePolygonIndex}
          onPolygonChange={handlePolygonChange}
          onAddPolygon={handleAddPolygon}
          onSelectPolygon={handleSelectPolygon}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          imageBackground={imageBackground}
        />
        
        <div className="flex flex-col w-72">
          <div className="flex items-center mb-4">
            <input
              type="text"
              value={newPolygonName}
              onChange={(e) => setNewPolygonName(e.target.value)}
              placeholder="New area name"
              className="border border-gray-300 rounded px-2 py-1 flex-1 mr-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Button
              onClick={handleAddPolygon}
              className="p-2 bg-blue-600 hover:bg-blue-700 transition-colors"
              title="Add new area"
            >
              <AiOutlinePlus size={18} />
            </Button>
          </div>

          <div className="space-y-2 mb-4 flex-grow overflow-y-auto">
            {polygons.map((polygon, index) => (
              <div
                key={polygon.id}
                className={`flex justify-between items-center p-3 rounded-lg ${index === activePolygonIndex
                  ? 'bg-blue-100 border-l-4 border-blue-500 shadow-md'
                  : 'bg-white border border-gray-200 hover:bg-gray-50 transition-colors'}`}
              >
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => handleSelectPolygon(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleSelectPolygon(index);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  {index === activePolygonIndex ? (
                    <input
                      type="text"
                      value={polygon.name || `Area ${index + 1}`}
                      onChange={(e) => handlePolygonNameChange(index, e.target.value)}
                      className="w-full p-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="font-medium">{polygon.name || `Area ${index + 1}`}</span>
                  )}
                </div>
                {index === activePolygonIndex && (
                  <div className="flex space-x-1 ml-2">
                    <Button
                      onClick={handleDeletePolygon}
                      className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors"
                      title="Delete area"
                    >
                      <AiOutlineDelete size={16} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {polygons.length === 0 && (
              <div className="text-center text-gray-500 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex flex-col items-center space-y-2">
                  <p>No areas defined yet. Click the + button to add an area.</p>
                </div>
              </div>
            )}
          </div>

          {/* <div className="flex justify-end mt-auto">
            <Button
              onClick={handleSavePolygons}
              className="px-4 py-2 flex items-center justify-center gap-2"
              disabled={polygons.length === 0}
            >
              <AiOutlineSave size={18} />
              Save Areas
            </Button>
          </div> */}
        </div>
      </div>
    </Card>
  );
};
